import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { dropTargetForExternal } from '@atlaskit/pragmatic-drag-and-drop/external/adapter';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { UrlValidationConfig } from '../../common/types';
import { validUrls } from '../../common/utils/url-validation';

import messages from './messages';
import { addFilesUpToMax } from './utils';

const styles = cssMap({
	wrapper: {
		position: 'relative',
	},
	fileDropzoneContentWrapper: {
		position: 'absolute',
		top: token('space.0'),
		left: token('space.0'),
		width: '100%',
		height: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		borderStyle: 'dashed',
		borderColor: token('color.border.focused'),
		borderRadius: token('radius.large'),
		zIndex: 1,
	},
});

type FileUploadDropzoneProps = {
	children: (props: {
		isDraggedOver: boolean;
		innerProps: {
			ref: React.RefObject<HTMLDivElement>;
			onPaste: (event: React.ClipboardEvent<HTMLDivElement>) => void;
		};
		/** This should be the last child of the wrapper */
		dropzoneContent: React.ReactNode;
	}) => React.ReactNode;
	maxFiles: number;
	currentFileCount: number;
	onFilesSelected: (files: File[]) => void;
	onUrlsSelected?: (urls: string[]) => void;
	disabled?: boolean;
	urlValidationConfig?: UrlValidationConfig;
};

export const FileUploadDropzone = ({
	children,
	maxFiles,
	currentFileCount,
	onFilesSelected,
	onUrlsSelected,
	disabled,
	urlValidationConfig,
}: FileUploadDropzoneProps): React.ReactNode => {
	const { formatMessage } = useIntl();
	const [isDraggedOver, setIsDraggedOver] = useState(false);
	const isDragAndDropEnabled = fg('phx_drag_and_drop_trello_cards_to_rovo_chat');
	const ref = useRef<HTMLDivElement>(null);
	const onFileSelectedRef = useRef<FileUploadDropzoneProps['onFilesSelected']>(onFilesSelected);
	const onUrlsSelectedRef = useRef<FileUploadDropzoneProps['onUrlsSelected']>(onUrlsSelected);
	const validatedUrlsRef = useRef<string[]>([]);

	onFileSelectedRef.current = onFilesSelected;
	onUrlsSelectedRef.current = onUrlsSelected;

	useEffect(() => {
		const element = ref.current;
		if (!element) {
			return;
		}

		const cleanupElements = isDragAndDropEnabled
			? dropTargetForElements({
					element,
					canDrop: () => !disabled,
					onDragEnter: ({ source: { data } }) => {
						const urls = data?.urls;
						if (Array.isArray(urls)) {
							const filteredUrls = validUrls(urls, urlValidationConfig);
							validatedUrlsRef.current = filteredUrls;

							if (filteredUrls.length > 0) {
								setIsDraggedOver(true);
							}
						}
					},
					onDragLeave: () => {
						validatedUrlsRef.current = [];
						setIsDraggedOver(false);
					},
					onDrop: () => {
						if (validatedUrlsRef.current.length > 0) {
							onUrlsSelectedRef.current?.(validatedUrlsRef.current);
						}
						validatedUrlsRef.current = [];
						setIsDraggedOver(false);
					},
				})
			: undefined;

		const cleanupExternal = dropTargetForExternal({
			element,
			canDrop: () => !disabled,
			onDragEnter: () => setIsDraggedOver(true),
			onDragLeave: () => setIsDraggedOver(false),
			onDrop: async ({ source: { items } }) => {
				setIsDraggedOver(false);

				const filePromises = await Promise.all(items.map((item) => item.getAsFile()));
				const rawFiles = filePromises.filter((file) => file !== null) as File[];

				const filesToAdd = addFilesUpToMax({
					addedFiles: rawFiles,
					currentFileCount,
					maxFileCount: maxFiles,
				});

				if (filesToAdd.length === 0) {
					return;
				}

				onFileSelectedRef.current(filesToAdd);
			},
		});

		return () => {
			cleanupElements?.();
			cleanupExternal();
		};
	}, [ref, currentFileCount, maxFiles, disabled, isDragAndDropEnabled, urlValidationConfig]);

	const onPaste = useCallback(
		(event: React.ClipboardEvent<HTMLDivElement>) => {
			const files = Array.from(event.clipboardData.files);

			// Rename pasted image files with incremental names (image1.png, image2.png, etc.)
			// instead of the browser-generated "image.png" to avoid missing files due to editor deduping by file name
			const processedFiles = files.map((file, index) => {
				// Only rename generic browser-generated image names like "image.png"
				const genericImagePattern = /^image\.\w+$/i;
				if (genericImagePattern.test(file.name)) {
					const extension = file.name.split('.').pop() || 'png';
					const newName = `image${currentFileCount + index + 1}.${extension}`;
					return new File([file], newName, { type: file.type });
				}
				return file;
			});

			const filesToAdd = addFilesUpToMax({
				addedFiles: processedFiles,
				currentFileCount,
				maxFileCount: maxFiles,
			});

			if (filesToAdd.length === 0) {
				return;
			}

			onFileSelectedRef.current(filesToAdd);
		},
		[currentFileCount, maxFiles],
	);

	return children({
		isDraggedOver,
		innerProps: { ref, onPaste },
		dropzoneContent: isDraggedOver ? (
			<Box
				xcss={styles.fileDropzoneContentWrapper}
				backgroundColor="color.background.accent.blue.subtlest"
			>
				<Text color="color.text.brand">{formatMessage(messages.dragDropMessage)}</Text>
			</Box>
		) : null,
	});
};
